import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import type { IconName, IconColor } from '../Icon';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  /** Visual style and semantic meaning of the toast. */
  variant: ToastVariant;
  /** The message to display. Rendered as body/medium typography. */
  message: string;
  /**
   * When true, renders a close/dismiss button on the right-hand side.
   * The button calls `onDismiss` when clicked.
   * Defaults to false.
   */
  showDismiss?: boolean;
  /** Called when the dismiss button is clicked. */
  onDismiss?: () => void;
}

// ─── Variant maps ─────────────────────────────────────────────────────────────

/**
 * Maps each variant to the Primer utility classes that control background and
 * border colour. These classes are defined in `src/primer/utilities.css` and
 * resolve through `--bg-color-*` / `--border-color-*` tokens, which means dark
 * theme overrides in `tailwind.dark.css` apply automatically.
 *
 * The component is sealed — no `className` prop is accepted. All visual states
 * are expressed through the `variant` prop using this map.
 */
const variantClasses: Record<ToastVariant, { bg: string; border: string }> = {
  // bg-success  → --bg-color-success   (green-100 light / green-800 dark)
  // border-success → --border-color-success (green-300 light / green-300 dark)
  success: { bg: 'bg-success', border: 'border-success' },
  // bg-error    → --bg-color-error     (magenta-100 light / magenta-1000 dark)
  // border-error   → --border-color-error   (magenta-800 light / magenta-300 dark)
  error: { bg: 'bg-error', border: 'border-error' },
  // bg-informative → --bg-color-informative (purple-200 light / neutral-800 dark)
  // border-default → --border-color-default (neutral-300 light / neutral-800 dark)
  info: { bg: 'bg-informative', border: 'border-default' },
  // bg-warning  → --bg-color-warning   (yellow-100 light / yellow-100 dark)
  // border-warning → --border-color-warning (yellow-300 light / yellow-300 dark)
  warning: { bg: 'bg-warning', border: 'border-warning' },
};

/**
 * Maps each variant to the icon to render and its Primer colour token.
 * Icon sizes: large = 24px (--spacing-5), matching the spec's `icon: size 24`.
 */
const variantIconConfig: Record<ToastVariant, { name: IconName; color: IconColor }> = {
  // circle-tick-filled — icon-success → --color-icon-success (green-500 light / green-200 dark)
  success: { name: 'circle-tick-filled', color: 'success' },
  // alert — icon-error → --color-icon-error (magenta-800 light / magenta-200 dark)
  error: { name: 'alert', color: 'error' },
  // circle-info-filled — icon-info → --color-icon-info (purple-800 light / purple-800 dark)
  info: { name: 'circle-info-filled', color: 'info' },
  // alert — icon-warning → --color-icon-warning (yellow-500 light / green-200 dark)
  warning: { name: 'alert', color: 'warning' },
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Toast component — presentational layer.
 *
 * Renders a single toast notification. This component is intentionally dumb:
 * it owns no timer logic, no positioning, and no portal. Those concerns live
 * in `ToastProvider`.
 *
 * The component is **sealed** — it does not accept a `className` prop. All
 * visual variation is expressed through the `variant` prop.
 *
 * For imperative usage (trigger toasts from anywhere in the app), use
 * `<ToastProvider>` at the root and `useToast()` in consuming components.
 *
 * @example
 * // Standalone — caller owns dismiss state
 * <Toast variant="success" message="Profile saved." showDismiss onDismiss={handleDismiss} />
 *
 * @example
 * // Imperative via hook
 * const { showToast } = useToast();
 * showToast({ variant: 'error', message: 'Something went wrong.', showDismiss: true });
 */
export function Toast({ variant, message, showDismiss = false, onDismiss }: ToastProps) {
  const { bg, border } = variantClasses[variant];
  const { name: iconName, color: iconColor } = variantIconConfig[variant];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        // Layout — flex row, vertically centred, consistent gap between icon and text
        'flex items-center gap-2',
        // Spacing — padding: spacing-4 (1rem / 16px) per spec
        'p-4',
        // Shape — border-radius: small (0.5rem / 8px) per spec; 1px solid border
        'rounded-small border',
        // Colours — resolved via variant map above
        bg,
        border
      )}
    >
      {/* Status icon — size large = 24px per spec; colour from variant map */}
      {/* shrink-0 prevents the icon from compressing when the message is long */}
      <Icon name={iconName} size="large" color={iconColor} className="shrink-0" />

      {/* Message — body/medium per spec; flex-1 so it fills remaining space */}
      <Typography variant="body" size="medium" className="flex-1">
        {message}
      </Typography>

      {/* Optional dismiss button — only rendered when showDismiss=true */}
      {/* ml-auto pushes it to the far right even when message is short */}
      {showDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className={cn(
            'ml-auto shrink-0',
            'flex items-center justify-center',
            // No background by default; transparent and borderless
            'bg-transparent border-none p-0',
            'cursor-pointer',
            // Subtle opacity dim on hover — avoids introducing a new bg token
            // on a small icon button that sits inside a coloured surface
            'opacity-100 hover:opacity-70 transition-opacity duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            'focus-visible:ring-(--border-color-interactiveloud)'
          )}
        >
          {/* close icon — icon-default → --color-icon-default per spec */}
          <Icon name="close" size="medium" color="default" />
        </button>
      )}
    </div>
  );
}
